import { PrimaryPhotoPool } from '../../utils/primaryPhotoPool';
import {
  primaryPhotoErrored,
  primaryPhotoFallback,
  primaryPhotoReady,
  primaryPhotoTimeout,
  primaryPhotoWaiting,
} from './segmentSlice';
import { mergeInventoryItem } from '../browse/browseSlice';
import type { AppDispatch } from '../../store/store';
import { mark, TimingMark } from '../../utils/clarityTimings';

/**
 * Module-scoped singleton that owns the post-confirm primary-photo
 * fan-out. Pool lives ABOVE the React tree so the user can navigate
 * away from {@link SegmentScreen} the instant the confirm response
 * lands — streams keep running, photos keep populating.
 *
 * Each `primary_photo_ready` event dispatches twice:
 *   1. Into segmentSlice (for the local DoneView's hero grid)
 *   2. Into browseSlice via mergeInventoryItem (for the Items grid
 *      / dashboard the user is now staring at)
 *
 * Two consumers, one source of truth — no cross-screen polling.
 *
 * Lifecycle:
 *   - `start(itemIds, dispatch)` allocates a fresh pool and enqueues
 *     every accepted id. Cancels any prior pool.
 *   - `cancel()` aborts every in-flight stream. Called on logout or
 *     when the user starts a new scan.
 *   - Survives `SegmentScreen` unmount on its own — no React refs.
 */

let pool: PrimaryPhotoPool | null = null;

export function startPrimaryPhotoStreams(
  itemIds: number[],
  dispatch: AppDispatch,
): void {
  // Cancel any prior pool to avoid stream-leak across confirms.
  pool?.cancelAll();

  pool = new PrimaryPhotoPool({
    maxConcurrent: 4,
    onEvent: (itemId, evt) => {
      switch (evt.type) {
        case 'waiting':
          dispatch(
            primaryPhotoWaiting({
              inventory_item_id: itemId,
              elapsed_seconds: evt.data.elapsed_seconds,
            }),
          );
          break;
        case 'primary_photo_ready':
          // FIRST_PRIMARY anchors at the first event (onlyFirst);
          // LAST_PRIMARY overwrites every time so by stream
          // termination it points at the most recent.
          mark(TimingMark.FIRST_PRIMARY, { onlyFirst: true });
          mark(TimingMark.LAST_PRIMARY);
          dispatch(
            primaryPhotoReady({
              inventory_item_id: itemId,
              primary_photo_url: evt.data.primary_photo_url,
              primary_photo_source: evt.data.primary_photo_source,
              elapsed_seconds: evt.data.elapsed_seconds,
            }),
          );
          // Also patch the BrowseSlice copy so the Items grid /
          // any other consumer sees the polished photo land in
          // real time.
          dispatch(
            mergeInventoryItem({
              id: itemId,
              primary_photo_url: evt.data.primary_photo_url,
              primary_photo_source: evt.data.primary_photo_source,
            }),
          );
          break;
        case 'primary_photo_fallback':
          dispatch(
            primaryPhotoFallback({
              inventory_item_id: itemId,
              primary_photo_source: evt.data.primary_photo_source,
              elapsed_seconds: evt.data.elapsed_seconds,
            }),
          );
          dispatch(
            mergeInventoryItem({
              id: itemId,
              primary_photo_source: evt.data.primary_photo_source,
            }),
          );
          break;
        case 'primary_photo_timeout':
          dispatch(
            primaryPhotoTimeout({
              inventory_item_id: itemId,
              elapsed_seconds: evt.data.elapsed_seconds,
              message: evt.data.message,
            }),
          );
          break;
      }
    },
    onError: (itemId, err: any) => {
      dispatch(
        primaryPhotoErrored({
          inventory_item_id: itemId,
          error: err?.message || 'stream failed',
        }),
      );
    },
  });

  for (const id of itemIds) pool.enqueue(id);
}

export function cancelPrimaryPhotoStreams(): void {
  pool?.cancelAll();
  pool = null;
}

/** True while at least one stream is in flight. Useful for telemetry
 *  / a "still polishing" indicator on a screen above the tree. */
export function isPolishing(): boolean {
  return pool !== null && pool.stats().inFlight > 0;
}
